/**
 * Admin view: users with permission matrix, filters, bulk actions, manage/revoke dialog.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  TablePagination,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  ExpandMore,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import PermissionGate from '../auth/PermissionGate';
import { useAuth } from '../auth/AuthProvider';
import adminApi from '../../services/adminApi';
import type { DataPermission, UserWithPermissionsItem } from '../../types';
import UserPermissionRevokeDialog from './UserPermissionRevokeDialog';
import AdminUserEditDialog from './AdminUserEditDialog';
import PermissionManagement from './PermissionManagement';
import './admin.css';

const EXPAND_COL_SPAN = 13;

function activeCount(items: { is_active: boolean }[] | undefined): number {
  if (!items?.length) return 0;
  return items.filter((x) => x.is_active).length;
}

/** API returns snake_case; tolerate camelCase if a client ever maps keys. */
function normalizeUser(u: UserWithPermissionsItem): UserWithPermissionsItem {
  const anyU = u as unknown as Record<string, unknown>;
  return {
    ...u,
    value_chain_access:
      u.value_chain_access ??
      (anyU.valueChainAccess as UserWithPermissionsItem['value_chain_access']) ??
      [],
    country_access:
      u.country_access ??
      (anyU.countryAccess as UserWithPermissionsItem['country_access']) ??
      [],
    data_permissions:
      u.data_permissions ??
      (anyU.dataPermissions as UserWithPermissionsItem['data_permissions']) ??
      [],
    financial_data_access:
      u.financial_data_access ??
      (anyU.financialDataAccess as UserWithPermissionsItem['financial_data_access']) ??
      [],
  };
}

function activeProdcomCount(data: DataPermission[] | undefined): number {
  if (!data?.length) return 0;
  return data.filter((p) => p.is_active && p.data_type === 'prodcom_view').length;
}

function activeGraphSourceCount(data: DataPermission[] | undefined): number {
  if (!data?.length) return 0;
  return data.filter((p) => p.is_active && p.data_type === 'graph_source').length;
}

function partitionDataPermissions(data: DataPermission[] | undefined): {
  prodcom: DataPermission[];
  graphSource: DataPermission[];
} {
  const list = data ?? [];
  const prodcom: DataPermission[] = [];
  const graphSource: DataPermission[] = [];
  for (const p of list) {
    if (p.data_type === 'prodcom_view') prodcom.push(p);
    else if (p.data_type === 'graph_source') graphSource.push(p);
  }
  return { prodcom, graphSource };
}

function formatLastLogin(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '—';
  }
}

function renderProdcomChips(items: DataPermission[]) {
  if (!items.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map((p) => (
        <Chip
          key={`pc-${p.id}`}
          size="small"
          label={`PRODCOM (${p.permission_level})`}
          variant={p.is_active ? 'filled' : 'outlined'}
        />
      ))}
    </Box>
  );
}

function renderGraphSourceChips(items: DataPermission[]) {
  if (!items.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map((p) => (
        <Chip
          key={`gs-${p.id}`}
          size="small"
          label={`${p.data_identifier} · ${p.permission_level}`}
          variant={p.is_active ? 'filled' : 'outlined'}
        />
      ))}
    </Box>
  );
}

interface RowProps {
  user: UserWithPermissionsItem;
  open: boolean;
  onToggle: () => void;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onManagePermissions: () => void;
  onEdit: () => void;
  onDelete: () => void;
  deleteDisabled: boolean;
}

function UserPermissionRow({
  user,
  open,
  onToggle,
  selected,
  onSelect,
  onManagePermissions,
  onEdit,
  onDelete,
  deleteDisabled,
}: RowProps) {
  const co = activeCount(user.country_access);
  const fi = activeCount(user.financial_data_access);
  const { prodcom, graphSource } = partitionDataPermissions(user.data_permissions);
  const valueChainsColumnCount =
    activeCount(user.value_chain_access) + activeGraphSourceCount(user.data_permissions);
  const prodcomActive = activeProdcomCount(user.data_permissions);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            inputProps={{ 'aria-label': `select ${user.email}` }}
          />
        </TableCell>
        <TableCell padding="checkbox" width={48}>
          <IconButton size="small" onClick={onToggle} aria-label="expand row">
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{user.full_name || '—'}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {user.company?.trim() ? user.company : '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip size="small" label={user.role || 'user'} variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={user.is_active ? 'Active' : 'Inactive'}
            color={user.is_active ? 'success' : 'default'}
            variant={user.is_active ? 'filled' : 'outlined'}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatLastLogin(user.last_login)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography component="span" variant="body2">
            {valueChainsColumnCount}
          </Typography>
        </TableCell>
        <TableCell align="center">{co}</TableCell>
        <TableCell align="center">
          <Typography component="span" variant="body2">
            {prodcomActive}
          </Typography>
        </TableCell>
        <TableCell align="center">{fi}</TableCell>
        <TableCell align="right">
          <Tooltip title="Manage permissions">
            <IconButton size="small" onClick={onManagePermissions} aria-label="manage permissions">
              <SecurityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit user">
            <IconButton size="small" onClick={onEdit} aria-label="edit user">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={deleteDisabled ? 'Cannot delete your own account' : 'Delete user'}>
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={deleteDisabled}
                aria-label="delete user"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : 0 }} colSpan={EXPAND_COL_SPAN}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, maxWidth: 720 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Value chains &amp; graph sources
              </Typography>
              {user.value_chain_access.length === 0 && graphSource.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  None
                </Typography>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Value chains
                  </Typography>
                  {user.value_chain_access.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      None
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                      {user.value_chain_access.map((p) => (
                        <Chip
                          key={p.id}
                          size="small"
                          label={`${p.value_chain_name} · ${p.permission_level}`}
                          variant={p.is_active ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  )}
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Graph sources
                  </Typography>
                  {graphSource.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      None
                    </Typography>
                  ) : (
                    renderGraphSourceChips(graphSource)
                  )}
                </Box>
              )}

              <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ mt: 1 }}>
                PRODCOM
              </Typography>
              {prodcom.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  None
                </Typography>
              ) : (
                <Box sx={{ mb: 2 }}>{renderProdcomChips(prodcom)}</Box>
              )}

              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Countries
              </Typography>
              {user.country_access.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {user.country_access.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={`${p.country_name} · ${p.permission_level}`}
                      variant={p.is_active ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              )}

              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Financial
              </Typography>
              {user.financial_data_access.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {user.financial_data_access.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={`${p.country} · ${p.source} · ${p.permission_level}`}
                      variant={p.is_active ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const DEFAULT_LIMIT = 25;

type StatusFilter = 'all' | 'active' | 'inactive';

const UserPermissionsOverview: React.FC = () => {
  const { user: authUser } = useAuth();
  const currentUserId = authUser?.id ?? 0;

  const [users, setUsers] = useState<UserWithPermissionsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [revokeUser, setRevokeUser] = useState<UserWithPermissionsItem | null>(null);
  const [editUser, setEditUser] = useState<UserWithPermissionsItem | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setSkip(0);
  }, [debouncedSearch, filterStatus]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, [skip, limit, debouncedSearch, filterStatus]);

  const isActiveParam =
    filterStatus === 'all' ? undefined : filterStatus === 'active';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getUsersWithPermissions({
        skip,
        limit,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(isActiveParam !== undefined ? { is_active: isActiveParam } : {}),
      });
      setUsers(res.users.map((u) => normalizeUser(u)));
      setTotal(res.total);
    } catch (e) {
      console.error(e);
      setError('Failed to load users and permissions');
    } finally {
      setLoading(false);
    }
  }, [skip, limit, debouncedSearch, isActiveParam]);

  useEffect(() => {
    load();
  }, [load]);

  const page = Math.floor(skip / limit);

  const toggleSelectUser = (id: number, checked: boolean) => {
    setSelectedUserIds((prev) => {
      if (checked) return Array.from(new Set([...prev, id]));
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAllPage = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds(users.map((u) => u.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const bulkDeleteUsers = async () => {
    if (!selectedUserIds.length) return;
    if (
      !window.confirm(
        `Delete ${selectedUserIds.length} selected user(s) on this page? This cannot be undone.`
      )
    ) {
      return;
    }
    const ids = [...selectedUserIds];
    const results = await Promise.allSettled(ids.map((id) => adminApi.deleteUser(id)));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    setSelectedUserIds([]);
    setExpandedId(null);
    await load();
    if (fail > 0) setError(`${fail} deletion(s) failed`);
  };

  const deleteOne = async (userId: number) => {
    if (!window.confirm('Permanently delete this user? This cannot be undone.')) return;
    try {
      await adminApi.deleteUser(userId);
      setSelectedUserIds((prev) => prev.filter((x) => x !== userId));
      setExpandedId((id) => (id === userId ? null : id));
      setRevokeUser((u) => (u?.id === userId ? null : u));
      setEditUser((u) => (u?.id === userId ? null : u));
      await load();
    } catch (e) {
      console.error(e);
      setError('Failed to delete user');
    }
  };

  const mergeUpdatedUser = (updated: UserWithPermissionsItem) => {
    const u = normalizeUser(updated);
    setUsers((prev) => prev.map((row) => (row.id === u.id ? u : row)));
    setRevokeUser((cur) => (cur?.id === u.id ? u : cur));
    setEditUser((cur) => (cur?.id === u.id ? u : cur));
  };

  const allPageSelected =
    users.length > 0 && users.every((u) => selectedUserIds.includes(u.id));
  const somePageSelected = users.some((u) => selectedUserIds.includes(u.id));

  return (
    <PermissionGate
      requiredPermissions={['admin']}
      fallback={
        <Alert severity="error">Admin permissions required for this view.</Alert>
      }
    >
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
              Users &amp; permissions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active permission counts only. Expand a row for details.
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => load()}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={bulkDeleteUsers}
              disabled={!selectedUserIds.length || loading}
            >
              Delete selected
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
          <TextField
            label="Search users"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="up-status-filter">Status</InputLabel>
            <Select
              labelId="up-status-filter"
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && users.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={somePageSelected && !allPageSelected}
                      checked={allPageSelected}
                      onChange={(e) => toggleSelectAllPage(e.target.checked)}
                      inputProps={{ 'aria-label': 'select all on page' }}
                    />
                  </TableCell>
                  <TableCell />
                  <TableCell>Name</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last login</TableCell>
                  <TableCell align="center">Value chains &amp; graph sources</TableCell>
                  <TableCell align="center">Countries</TableCell>
                  <TableCell align="center">PRODCOM</TableCell>
                  <TableCell align="center">Financial</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <UserPermissionRow
                    key={u.id}
                    user={u}
                    open={expandedId === u.id}
                    onToggle={() =>
                      setExpandedId((id) => (id === u.id ? null : u.id))
                    }
                    selected={selectedUserIds.includes(u.id)}
                    onSelect={(checked) => toggleSelectUser(u.id, checked)}
                    onManagePermissions={() => setRevokeUser(u)}
                    onEdit={() => setEditUser(u)}
                    onDelete={() => deleteOne(u.id)}
                    deleteDisabled={u.id === currentUserId}
                  />
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setSkip(newPage * limit)}
              rowsPerPage={limit}
              onRowsPerPageChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setSkip(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        )}

        <Accordion defaultExpanded={false} sx={{ mt: 2 }} disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" component="span">
              Bulk permission records
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              sx={{ ml: 1.5 }}
            >
              Value chains, countries, data access, financial — same tools as the former tab.
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>
            <PermissionManagement embedded onEmbeddedDataChanged={load} />
          </AccordionDetails>
        </Accordion>

        <UserPermissionRevokeDialog
          open={Boolean(revokeUser)}
          user={revokeUser}
          onClose={() => setRevokeUser(null)}
          onPermissionsChanged={() => {
            load();
          }}
        />

        <AdminUserEditDialog
          open={Boolean(editUser)}
          user={editUser}
          currentUserId={currentUserId}
          onClose={() => setEditUser(null)}
          onSaved={(updated) => {
            mergeUpdatedUser(updated);
          }}
        />
      </Box>
    </PermissionGate>
  );
};

export default UserPermissionsOverview;
